import React, { useCallback, useMemo } from "react";
import { SensorDataFile, SensorDataSession, Station } from "@shared/models";
import { SidebarBackButton } from "@shared/components/Sidebar/components/SidebarBackButton";
import { Button, FieldPreview } from "@shared/components";
import { getFormattedDate } from "@shared/utils";
import { Checkbox, CheckboxGroup } from "rsuite";
import { ValueType } from "rsuite/esm/Checkbox";

import "./index.scss";

export const MAX_SELECTED_FILES = 2;

interface SensorDataSessionDetailsSidebarProps {
  appliedFiles: SensorDataFile[];
  appliedStations: Station[];
  session: SensorDataSession;
  onEditSessionClick?: (patient: SensorDataSession | null) => void;
  onApplySelectedFiles: (files: number[], stations: string[]) => void;
  onBackBtnClick?: () => void;
}

const SensorDataSessionDetailsSidebar = (props: SensorDataSessionDetailsSidebarProps) => {
  const { session, onEditSessionClick, onBackBtnClick, onApplySelectedFiles, appliedFiles, appliedStations } = props;

  const [selectedFiles, setSelectedFiles] = React.useState<number[]>([]);
  const [selectedStations, setSelectedStations] = React.useState<string[]>([]);

  useMemo(() => {
    setSelectedFiles(appliedFiles.map((f) => f.id));
  }, [appliedFiles]);

  useMemo(() => {
    setSelectedStations(appliedStations.map((s) => s.station_code));
  }, [appliedStations]);

  const handleEditBtnClick = useCallback(() => {
    onEditSessionClick && onEditSessionClick(session);
  }, [session, onEditSessionClick]);

  const handleBackBtnClick = useCallback(() => {
    onBackBtnClick && onBackBtnClick();
  }, [onBackBtnClick]);

  const handleSelectFileChange = useCallback((values: ValueType[]) => {
    setSelectedStations([]);

    if (values.length > MAX_SELECTED_FILES) {
      return setSelectedFiles(values.slice(values.length - MAX_SELECTED_FILES) as number[]);
    }

    setSelectedFiles(values as number[]);
  }, []);

  const handleSelectSessionChange = useCallback((values: ValueType[]) => {
    setSelectedFiles([]);
    setSelectedStations(values as string[]);
  }, []);

  return (
    <div className="session-sidebar-container">
      <SidebarBackButton onClick={handleBackBtnClick} />

      <div className="session-sidebar-content">
        <div className="session-sidebar-session-id">{session?.name}</div>
        <FieldPreview
          className="session-sidebar-field"
          label="Date Created"
          value={session ? getFormattedDate(session.created_at) : "-"}
        />
        <FieldPreview className="session-sidebar-field" label="Created By" value={session?.created_by || "-"} />
        {session.files.length ? (
          <>
            <hr />
            <FieldPreview className="session-sidebar-field files" label="Files" />
            <CheckboxGroup name="checkboxList" value={selectedFiles} onChange={handleSelectFileChange}>
              {session.files.map((item) => (
                <Checkbox key={item.id} value={item.id}>
                  {item.custom_name || item.name}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </>
        ) : null}
        {session.station ? (
          <>
            <hr className="stations-hr" />
            <FieldPreview className="session-sidebar-field files" label="Smart Data Hub" />
            <CheckboxGroup name="checkboxList" value={selectedStations} onChange={handleSelectSessionChange}>
              {[session.station].map((station) => (
                <Checkbox key={station} value={station}>
                  {station}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </>
        ) : null}
        {session.files.length ? (
          <div className="apply-btn">
            <Button
              className="apply-session-btn full-width"
              onClick={() => onApplySelectedFiles(selectedFiles, selectedStations)}
            >
              Apply
            </Button>
          </div>
        ) : null}
        <Button
          disabled={!session || !onEditSessionClick}
          className="edit-session-btn outline-blue full-width"
          onClick={handleEditBtnClick}
        >
          Edit Session
        </Button>
      </div>
    </div>
  );
};

export default SensorDataSessionDetailsSidebar;
